import { Link } from '@tanstack/react-router';
import { CircleCheck } from 'lucide-react';
import { useState } from 'react';
import { useForm, type Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useApplyFieldErrors } from '@nucleus-commerce/admin-kit/errors';
import type {
  ApproveApplicationResponse,
  BrandApplication,
} from '@nucleus-commerce/api-client/platform';
import { Alert, AlertDescription, AlertTitle } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { Switch } from '@nucleus-commerce/ui/components/switch';

import { ProvisionWarnings } from '@/features/organizations';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { approveFormFor, TIMEZONES, toApproveRequest } from '../api/mappers';
import { useApproveApplication } from '../hooks/use-approve-application';
import { approveSchema, type ApproveInput, type ApproveOutput } from '../schemas/approve-schema';

export interface ApproveApplicationDialogProps {
  application: BrandApplication;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Approving provisions the organization, its first store and the applicant as invited owner.
 * The form is prefilled from the application; after success the dialog links to the new
 * organization.
 */
export function ApproveApplicationDialog({
  application,
  open,
  onOpenChange,
}: ApproveApplicationDialogProps) {
  const { t } = useTranslation(['applications', 'common']);
  const [approved, setApproved] = useState<{
    result: ApproveApplicationResponse;
    currency: string | undefined;
  } | null>(null);
  const result = approved?.result ?? null;
  const close = () => onOpenChange(false);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setApproved(null);
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-h-[90svh] overflow-y-auto sm:max-w-lg">
        {result ? (
          <>
            <DialogHeader>
              <DialogTitle>{t('applications:approve.doneTitle')}</DialogTitle>
              <DialogDescription>
                {t('applications:approve.doneDescription', { name: result.organization.name })}
              </DialogDescription>
            </DialogHeader>
            <Alert role="status">
              <CircleCheck aria-hidden />
              <AlertTitle>{result.organization.name}</AlertTitle>
              <AlertDescription>
                {result.owner.invited
                  ? t('applications:approve.ownerInvited', { email: result.owner.email })
                  : result.owner.created
                    ? t('applications:approve.ownerNotEmailed', { email: result.owner.email })
                    : t('applications:approve.ownerExisting', { email: result.owner.email })}
              </AlertDescription>
            </Alert>
            <ProvisionWarnings warnings={result.warnings} currency={approved?.currency} />
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={close}>
                {t('common:actions.close')}
              </Button>
              <Button asChild>
                <Link
                  to="/organizations/$organizationId"
                  params={{ organizationId: result.organization.id }}
                >
                  {t('applications:approve.openOrganization')}
                </Link>
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>
                {t('applications:approve.title', { brand: application.brandName })}
              </DialogTitle>
              <DialogDescription>{t('applications:approve.description')}</DialogDescription>
            </DialogHeader>
            {open ? (
              <ApproveForm application={application} onApproved={setApproved} onCancel={close} />
            ) : null}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface ApproveFormProps {
  application: BrandApplication;
  onApproved: (approved: { result: ApproveApplicationResponse; currency: string }) => void;
  onCancel: () => void;
}

interface TextSpec {
  name: Extract<Path<ApproveInput>, 'slug' | 'storeName' | 'currency' | 'country'>;
  hint?: boolean;
}

const TEXT_FIELDS: TextSpec[] = [
  { name: 'slug', hint: true },
  { name: 'storeName', hint: true },
  { name: 'currency', hint: true },
  { name: 'country', hint: true },
];

function ApproveForm({ application, onApproved, onCancel }: ApproveFormProps) {
  const { t } = useTranslation(['applications', 'common']);
  const approve = useApproveApplication(application.id);
  const form = useForm<ApproveInput, unknown, ApproveOutput>({
    resolver: useI18nResolver(approveSchema),
    defaultValues: approveFormFor(application),
  });
  const applyFieldErrors = useApplyFieldErrors(form.setError);

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      const body = toApproveRequest(values);
      const result = await approve.mutateAsync(body);
      // The backend falls back to the application's currency, then USD, as the form hint says.
      onApproved({ result, currency: body.currency ?? application.currency ?? 'USD' });
    } catch (error) {
      applyFieldErrors(error);
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        {TEXT_FIELDS.map((spec) => (
          <FormField
            key={spec.name}
            control={form.control}
            name={spec.name}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t(`applications:approve.fields.${spec.name}`)}</FormLabel>
                <FormControl>
                  <Input autoComplete="off" {...field} />
                </FormControl>
                {spec.hint ? (
                  <FormDescription>
                    {t(`applications:approve.fields.${spec.name}Hint`)}
                  </FormDescription>
                ) : null}
                <FormMessage />
              </FormItem>
            )}
          />
        ))}
        <FormField
          control={form.control}
          name="timezone"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('applications:approve.fields.timezone')}</FormLabel>
              <Select value={field.value} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {TIMEZONES.map((zone) => (
                    <SelectItem key={zone} value={zone}>
                      {zone.replaceAll('_', ' ')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="sendInvite"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start justify-between gap-4 rounded-lg border p-3">
              <div className="flex flex-col gap-1">
                <FormLabel>{t('applications:approve.fields.sendInvite')}</FormLabel>
                <FormDescription>
                  {t('applications:approve.fields.sendInviteHint', {
                    email: application.ownerEmail,
                  })}
                </FormDescription>
              </div>
              <FormControl>
                <Switch checked={field.value} onCheckedChange={field.onChange} />
              </FormControl>
            </FormItem>
          )}
        />
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onCancel}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="submit" disabled={approve.isPending}>
            {approve.isPending
              ? t('applications:approve.confirming')
              : t('applications:approve.confirm')}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
